import React, { useState, useEffect } from 'react';
import { Modal, Button, Form } from 'react-bootstrap';
import { Formik } from 'formik';
import * as Yup from 'yup';
import Select from 'react-select';
import { toast } from 'react-toastify';
import { customStyles, DropdownIndicator } from '~/Styles/reactSelectStyles';
import { extrairMensagemErro } from '~/Utils/Helpers';
import * as api from '~/Services/Service';

const validationSchema = Yup.object().shape({
  codigo_grupo_empresa: Yup.number()
    .typeError('Grupo de empresa é obrigatório')
    .required('Grupo de empresa é obrigatório')
    .positive()
    .integer(),
  codigo_portador_juridico: Yup.number()
    .typeError('Portador Jurídico é obrigatório')
    .required('Portador Jurídico é obrigatório')
    .positive()
    .integer(),
  codigo_portador: Yup.number()
    .typeError('Portador é obrigatório')
    .required('Portador é obrigatório')
    .positive()
    .integer()
    .test(
      'portador-diferente',
      'O portador e o portador jurídico não podem ser a mesma conta.',
      function testarPortadorDiferente(value) {
        const juridico = this.parent.codigo_portador_juridico;
        if (value == null || juridico == null) return true;
        return Number(value) !== Number(juridico);
      }
    ),
});

const initialValues = {
  codigo_grupo_empresa: '',
  codigo_portador_juridico: '',
  codigo_portador: '',
};

const ModalIncluir = ({ mostrar, onFechar, onSalvar, gruposEmpresa }) => {
  const [contasJuridicas, setContasJuridicas] = useState([]);
  const [portadores, setPortadores] = useState([]);
  const [carregandoCombos, setCarregandoCombos] = useState(false);

  // Reset combos ao abrir/fechar
  useEffect(() => {
    if (!mostrar) {
      setContasJuridicas([]);
      setPortadores([]);
    }
  }, [mostrar]);

  // Cascata GE → combos (anti-race: LICOES ciclo 9 L3)
  const carregarCombosPorGE = (codigoGE, setFieldValue) => {
    setFieldValue('codigo_portador_juridico', '');
    setFieldValue('codigo_portador', '');
    setContasJuridicas([]);
    setPortadores([]);
    if (!codigoGE) return undefined;

    let ativo = true;
    setCarregandoCombos(true);
    Promise.all([
      api.listarContasJuridicas(codigoGE),
      api.listarPortadoresPorGE(codigoGE),
    ])
      .then(([respJur, respPort]) => {
        if (!ativo) return;
        setContasJuridicas(
          (respJur || []).map(c => ({
            value: c.codigo_conta,
            label: `${c.codigo_conta} - ${c.apelido}`,
          }))
        );
        setPortadores(
          (respPort || []).map(c => ({
            value: c.codigo_conta,
            label: `${c.codigo_conta} - ${c.apelido}`,
          }))
        );
      })
      .catch(err => {
        if (!ativo) return;
        toast.error(
          extrairMensagemErro(err) || 'Erro ao carregar contas para o GE.'
        );
      })
      .finally(() => {
        if (ativo) setCarregandoCombos(false);
      });

    return () => {
      ativo = false;
    };
  };

  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    try {
      await api.incluirPortadorJuridico({
        codigo_grupo_empresa: values.codigo_grupo_empresa,
        codigo_portador: values.codigo_portador,
        codigo_portador_juridico: values.codigo_portador_juridico,
      });
      resetForm();
      setContasJuridicas([]);
      setPortadores([]);
      onSalvar();
    } catch (err) {
      // ADR-003: 409 (duplicata) ou outras validações — manter modal aberto
      const mensagem =
        err && err.codigo === 'DUPLICATA'
          ? err.mensagem || 'Vínculo já existe.'
          : extrairMensagemErro(err) || 'Erro ao incluir vínculo.';
      toast.error(mensagem);
    } finally {
      setSubmitting(false);
    }
  };

  const handleFechar = resetForm => {
    setContasJuridicas([]);
    setPortadores([]);
    resetForm();
    onFechar();
  };

  // RN-6: se há apenas 1 GE, pré-selecionar nos valores iniciais
  const valoresIniciais =
    gruposEmpresa && gruposEmpresa.length === 1
      ? {
          ...initialValues,
          codigo_grupo_empresa: gruposEmpresa[0].value,
        }
      : initialValues;

  return (
    <Formik
      initialValues={valoresIniciais}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
      enableReinitialize
    >
      {({
        values,
        errors,
        touched,
        setFieldValue,
        handleSubmit: formikSubmit,
        isSubmitting,
        resetForm,
      }) => {
        const geSelecionado =
          gruposEmpresa.find(g => g.value === values.codigo_grupo_empresa) ||
          null;
        const portadorJuridicoSelecionado =
          contasJuridicas.find(
            c => c.value === values.codigo_portador_juridico
          ) || null;
        const portadorSelecionado =
          portadores.find(c => c.value === values.codigo_portador) || null;

        const placeholderJuridico = !values.codigo_grupo_empresa
          ? 'Selecione um GE primeiro'
          : carregandoCombos
            ? 'Carregando...'
            : 'Selecione o Portador Jurídico...';
        const placeholderPortador = !values.codigo_grupo_empresa
          ? 'Selecione um GE primeiro'
          : carregandoCombos
            ? 'Carregando...'
            : 'Selecione o Portador...';

        // Auto-disparar cascata quando o GE já vem pré-selecionado e os combos ainda estão vazios
        const dispararCascataInicial = () => {
          if (
            values.codigo_grupo_empresa &&
            contasJuridicas.length === 0 &&
            portadores.length === 0 &&
            !carregandoCombos
          ) {
            carregarCombosPorGE(values.codigo_grupo_empresa, setFieldValue);
          }
        };

        return (
          <Modal
            show={mostrar}
            onHide={() => handleFechar(resetForm)}
            onEntered={dispararCascataInicial}
            backdrop="static"
          >
            <Modal.Header closeButton>
              <Modal.Title>Novo Vínculo Portador Jurídico</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <Form
                onSubmit={evt => {
                  evt.preventDefault();
                  formikSubmit();
                }}
              >
                <Form.Group className="mb-3">
                  <Form.Label>
                    Grupo de Empresa <span className="text-danger">*</span>
                  </Form.Label>
                  <Select
                    placeholder="Selecione um GE..."
                    options={gruposEmpresa}
                    value={geSelecionado}
                    onChange={opt => {
                      const valor = opt ? opt.value : '';
                      setFieldValue('codigo_grupo_empresa', valor);
                      carregarCombosPorGE(valor, setFieldValue);
                    }}
                    components={{ DropdownIndicator }}
                    styles={customStyles}
                    isClearable
                  />
                  {touched.codigo_grupo_empresa && errors.codigo_grupo_empresa && (
                    <div className="text-danger small mt-1">
                      {errors.codigo_grupo_empresa}
                    </div>
                  )}
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>
                    Portador Jurídico <span className="text-danger">*</span>
                  </Form.Label>
                  <Select
                    placeholder={placeholderJuridico}
                    options={contasJuridicas}
                    value={portadorJuridicoSelecionado}
                    onChange={opt =>
                      setFieldValue(
                        'codigo_portador_juridico',
                        opt ? opt.value : ''
                      )
                    }
                    isDisabled={
                      !values.codigo_grupo_empresa || carregandoCombos
                    }
                    isLoading={carregandoCombos}
                    components={{ DropdownIndicator }}
                    styles={customStyles}
                    isClearable
                    noOptionsMessage={() =>
                      'Nenhuma conta jurídica disponível para este GE'
                    }
                  />
                  {touched.codigo_portador_juridico &&
                    errors.codigo_portador_juridico && (
                      <div className="text-danger small mt-1">
                        {errors.codigo_portador_juridico}
                      </div>
                    )}
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>
                    Portador <span className="text-danger">*</span>
                  </Form.Label>
                  <Select
                    placeholder={placeholderPortador}
                    options={portadores}
                    value={portadorSelecionado}
                    onChange={opt =>
                      setFieldValue('codigo_portador', opt ? opt.value : '')
                    }
                    isDisabled={
                      !values.codigo_grupo_empresa || carregandoCombos
                    }
                    isLoading={carregandoCombos}
                    components={{ DropdownIndicator }}
                    styles={customStyles}
                    isClearable
                    noOptionsMessage={() =>
                      'Nenhum portador disponível para este GE'
                    }
                  />
                  {touched.codigo_portador && errors.codigo_portador && (
                    <div className="text-danger small mt-1">
                      {errors.codigo_portador}
                    </div>
                  )}
                </Form.Group>
              </Form>
            </Modal.Body>
            <Modal.Footer>
              <Button
                variant="secondary"
                onClick={() => handleFechar(resetForm)}
              >
                Cancelar
              </Button>
              <Button
                variant="primary"
                onClick={formikSubmit}
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Salvando...' : 'Salvar'}
              </Button>
            </Modal.Footer>
          </Modal>
        );
      }}
    </Formik>
  );
};

export default ModalIncluir;
